import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoQuestionsResult,
  PostVideoQuestionsOptions,
  GetVideoQuestionsOptions,
  GetVideoQuestionsResult,
  getVideoQuestions,
  PostVideoQuestionsFields,
  GetVideoQuestionsFields,
  postVideoQuestions,
} from '../fetchers/video/questions';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoQuestionsOptions<Fields extends GetVideoQuestionsFields> =
  GetVideoQuestionsOptions<Fields>;

/**
 * Get a list of automatically generated questions about a video
 * This method returns a list of automatically generated questions about the specified video. The owner of the video must have AI viewer permissions.
 *
 * This method works only for videos with completed transcripts in English.
 */

export function useGetVideoQuestions<Fields extends GetVideoQuestionsFields>(
  getOptions:
    | UseGetVideoQuestionsOptions<Fields>
    | (() => UseGetVideoQuestionsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoQuestionsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/videos/${options.where.videoId}/questions${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getVideoQuestions({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoQuestions, {
    endpoint: '/videos/:videoId/questions',
    method: 'GET',
  });
}

/**
 * Get a list of automatically generated questions about a video
 * This method returns a list of automatically generated questions about the specified video. The owner of the video must have AI viewer permissions.
 *
 * This method works only for videos with completed transcripts in English.
 */

export function useGetVideoQuestionsLazy<Fields extends GetVideoQuestionsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoQuestionsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoQuestionsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/questions${serializeQuery(options)}`,
          getVideoQuestions({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoQuestionsLazy, {
    endpoint: '/videos/:videoId/questions',
    method: 'GET',
  });
}

type UsePostVideoQuestionsOptions<Fields extends PostVideoQuestionsFields> =
  PostVideoQuestionsOptions<Fields>;

/**
 * Get an automated answer to the user's question about a video
 * This method returns an automated answer to the user's question about the specified video.
 *
 * This method works only for videos with completed transcripts in English.
 */

export function usePostVideoQuestions<Fields extends PostVideoQuestionsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoQuestionsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoQuestionsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/questions${serializeQuery(options)}`,
          postVideoQuestions({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoQuestions, {
    endpoint: '/videos/:videoId/questions',
    method: 'POST',
  });
}
